import {
  ArchiveIcon,
  ArrowLeftIcon,
  PaperClipIcon,
  ReplyIcon,
  TrashIcon,
} from '@heroicons/react/outline';

import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import type { DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';
import { delay, tone } from '../../primitives';

/**
 * A customer's email and, under it in the same thread, the reply the agent
 * saved as a draft, signed by the business, waiting for one click on Send.
 */
export default function EmailThread({
  brand,
  subject,
  customer,
  body,
  reply,
  signature,
}: {
  brand: DemoBrand;
  subject: string;
  customer: { name: string; email: string };
  body: string;
  reply: string;
  signature: string;
}) {
  const { t } = useChannelsT();

  return (
    <div
      className={clsxm(
        'overflow-hidden rounded-[22px] text-start shadow-[0_40px_90px_-30px_rgba(22,12,90,0.75)]',
        tone.surface,
        tone.line,
      )}
    >
      <div
        className={clsxm(
          'flex items-center gap-4 border-b border-black/[0.06] px-5 py-3 dark:border-white/[0.06]',
          tone.quiet,
        )}
      >
        <ArrowLeftIcon className='h-4 w-4 rtl:-scale-x-100' />
        <ArchiveIcon className='h-4 w-4' />
        <TrashIcon className='h-4 w-4' />
      </div>
      <div className='px-5 pb-5 pt-4'>
        <p
          className={clsxm(
            'flex flex-wrap items-center gap-2 text-[17px] font-semibold leading-snug',
            tone.ink,
          )}
        >
          {subject}
          <span className='rounded bg-[#f1f1f5] px-1.5 py-0.5 text-[11px] font-medium text-[#56566d] dark:bg-white/10 dark:text-[#a4a4bd]'>
            {t('Inbox')}
          </span>
        </p>

        <div className='mt-4 flex gap-3'>
          <span className='flex h-9 w-9 flex-none items-center justify-center rounded-full bg-[linear-gradient(140deg,#38bdf8,#1d4ed8)] text-[14px] font-semibold text-white'>
            {customer.name.charAt(0)}
          </span>
          <div className='min-w-0 flex-1'>
            <p className='flex items-baseline justify-between gap-3'>
              <span
                className={clsxm(
                  'truncate text-[13.5px] font-semibold',
                  tone.ink,
                )}
              >
                {customer.name}{' '}
                <span className={clsxm('font-normal', tone.quiet)}>
                  {'<'}
                  <span dir='ltr'>{customer.email}</span>
                  {'>'}
                </span>
              </span>
              <span
                dir='ltr'
                className={clsxm('flex-none text-xs tabular-nums', tone.quiet)}
              >
                10:12
              </span>
            </p>
            <p
              className={clsxm('mt-2 text-[13.5px] leading-relaxed', tone.body)}
            >
              {body}
            </p>
          </div>
        </div>

        <div
          className={clsxm(
            styles.bubble,
            'mt-5 rounded-2xl p-4 ring-1 ring-inset ring-[#0f0f28]/10 dark:ring-white/10',
          )}
          style={delay(1900)}
        >
          <p className='flex items-center gap-2 text-[13px]'>
            <ReplyIcon
              className={clsxm('h-4 w-4 rtl:-scale-x-100', tone.quiet)}
            />
            <span className='font-semibold text-[#d93025] dark:text-[#f28b82]'>
              {t('Draft')}
            </span>
            <span className={clsxm('truncate', tone.quiet)}>
              {t('to {{name}}', { name: customer.name })}
            </span>
          </p>
          <p className={clsxm('mt-3 text-[13.5px] leading-relaxed', tone.ink)}>
            {reply}
          </p>
          <p className={clsxm('mt-3 text-[13px] leading-snug', tone.quiet)}>
            {signature}
            <br />
            {brand.name}
          </p>
          <div className='mt-4 flex items-center gap-3'>
            <span className='rounded-full bg-[#0b57d0] px-5 py-2 text-[13px] font-semibold text-white'>
              {t('Send')}
            </span>
            <PaperClipIcon className={clsxm('h-4 w-4', tone.quiet)} />
            <TrashIcon className={clsxm('ms-auto h-4 w-4', tone.quiet)} />
          </div>
        </div>
      </div>
    </div>
  );
}
